<template>
  <div class="pass-wrap">
    <div
      class="pass-card"
      :style="{
        backgroundColor: backgroundColor,
        color: foregroundColor,
      }"
    >
      <!-- Header -->
      <div class="pass-header">
        <div class="pass-logo">
          <img
            v-if="logoUrl"
            :src="logoUrl"
            :alt="`${displayName || 'Business'} logo`"
            class="pass-logo-img"
          />
          <span
            v-else
            class="pass-logo-placeholder"
            :style="{ borderColor: foregroundColor, color: foregroundColor }"
          >
            Logo
          </span>
        </div>
        <span class="pass-business-name" :style="{ color: foregroundColor }">
          {{ displayName || 'Business Name' }}
        </span>
      </div>

      <!-- Stamp grid -->
      <div
        class="pass-stamps"
        :style="{
          gridTemplateColumns: `repeat(${stampCols}, ${stampSize}px)`,
          gap: stampGap + 'px',
        }"
      >
        <div
          v-for="i in resolvedThreshold"
          :key="i"
          class="stamp-circle"
          :style="{
            width: stampSize + 'px',
            height: stampSize + 'px',
            border: `${stampBorder}px solid ${foregroundColor}`,
            backgroundColor: i <= filledStamps ? foregroundColor : 'transparent',
            boxShadow:
              i <= filledStamps
                ? `inset 0 0 0 ${stampInset}px ${backgroundColor}`
                : 'none',
          }"
        ></div>
      </div>

      <!-- Member field -->
      <div class="pass-field">
        <span class="pass-field-label" :style="{ color: foregroundColor }">MEMBER</span>
        <span class="pass-field-value" :style="{ color: foregroundColor }">
          john.doe@gmail.com
        </span>
      </div>

      <!-- Auxiliary field -->
      <div v-if="auxiliaryFieldLabel" class="pass-field">
        <span class="pass-field-label" :style="{ color: foregroundColor }">
          {{ auxiliaryFieldLabel.toUpperCase() }}
        </span>
        <span class="pass-field-value" :style="{ color: foregroundColor }">
          {{ auxiliaryFieldValue || '-' }}
        </span>
      </div>

      <!-- QR code -->
      <div class="pass-qr-wrap">
        <div class="pass-qr-box">
          <svg
            class="pass-qr-svg"
            viewBox="0 0 21 21"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <!-- Finder pattern outer squares -->
            <g fill="#000">
              <rect x="0" y="0" width="7" height="7" />
              <rect x="14" y="0" width="7" height="7" />
              <rect x="0" y="14" width="7" height="7" />
            </g>
            <g fill="#fff">
              <rect x="1" y="1" width="5" height="5" />
              <rect x="15" y="1" width="5" height="5" />
              <rect x="1" y="15" width="5" height="5" />
            </g>
            <g fill="#000">
              <rect x="2" y="2" width="3" height="3" />
              <rect x="16" y="2" width="3" height="3" />
              <rect x="2" y="16" width="3" height="3" />
            </g>
            <!-- Decorative data cells -->
            <g fill="#000">
              <rect
                v-for="(cell, idx) in qrCells"
                :key="idx"
                :x="cell.x"
                :y="cell.y"
                width="1"
                height="1"
              />
            </g>
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'RewardsCardPreview',
  props: {
    displayName: { type: String, default: '' },
    logoUrl: { type: String, default: null },
    backgroundColor: { type: String, default: '#ffffff' },
    foregroundColor: { type: String, default: '#000000' },
    thresholdForRedeeming: { type: Number, default: 8 },
    auxiliaryFieldLabel: { type: String, default: null },
    auxiliaryFieldValue: { type: String, default: null },
  },
  setup(props) {
    const resolvedThreshold = computed(() => props.thresholdForRedeeming || 8);

    // Always lay out the stamps in 2 rows; columns scale with threshold.
    const stampCols = computed(() => Math.ceil(resolvedThreshold.value / 2));

    // Card has ~236px usable width inside padding. Stamps grow up to ~42px
    // each, shrinking only when the threshold exceeds what comfortably fits.
    // Cap is intentionally lower than the absolute fit so the card layout
    // stays balanced at low thresholds.
    const stampGap = 6;
    const stampSize = computed(() => {
      const available = 236;
      const cols = stampCols.value;
      return Math.min(42, Math.floor((available - (cols - 1) * stampGap) / cols));
    });

    // Border + inset scale with stamp size so filled stamps keep their
    // ring-around-a-dot look at any threshold.
    const stampBorder = computed(() => Math.max(2, Math.round(stampSize.value * 0.08)));
    const stampInset = computed(() => Math.max(2, Math.round(stampSize.value * 0.09)));

    // Show a couple of filled stamps to convey "in progress".
    const filledStamps = computed(() => Math.min(2, resolvedThreshold.value));

    // Decorative QR code data cells - deterministic so the preview is stable.
    // Skips the three finder regions (drawn separately) and uses a cheap
    // hash to scatter "data" pixels across the rest of the 21x21 grid.
    const qrCells = computed(() => {
      const cells = [];
      const inFinder = (x, y) =>
        (x <= 7 && y <= 7) || (x >= 13 && y <= 7) || (x <= 7 && y >= 13);
      for (let y = 0; y < 21; y++) {
        for (let x = 0; x < 21; x++) {
          if (inFinder(x, y)) continue;
          const hash = (x * 53 + y * 31 + x * y * 7) % 100;
          if (hash < 47) cells.push({ x, y });
        }
      }
      return cells;
    });

    return {
      resolvedThreshold,
      stampCols,
      stampSize,
      stampGap,
      stampBorder,
      stampInset,
      filledStamps,
      qrCells,
    };
  },
};
</script>

<style scoped>
.pass-wrap {
  width: 280px;
  margin: 0 auto;
  position: relative;
  /* Keeps the shadow pseudo-element behind the card but inside this wrapper. */
  isolation: isolate;
}

/* The card is masked to cut the notch, and a mask clips box-shadow, while a
   `filter: drop-shadow` through a mask renders as a hard, unblurred offset
   copy in Safari. So the shadow is a plain rounded rectangle painted behind
   the card instead. */
.pass-wrap::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 20px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
  z-index: -1;
}

.pass-card {
  width: 280px;
  min-height: 410px;
  height: auto;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  border-radius: 20px;
  padding: 22px 22px 18px;
  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
  overflow: hidden;
  /* Cut a small semicircular notch out of the top centre to mimic the
     Apple Wallet pass envelope. */
  -webkit-mask-image: radial-gradient(
    circle 14px at 50% 0,
    transparent 99%,
    #000 100%
  );
  mask-image: radial-gradient(
    circle 14px at 50% 0,
    transparent 99%,
    #000 100%
  );
}

.pass-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  padding-top: 4px;
}

.pass-logo {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pass-logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.pass-logo-placeholder {
  width: 100%;
  height: 100%;
  border: 1.5px dashed;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  opacity: 0.6;
}

.pass-business-name {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pass-stamps {
  display: grid;
  justify-content: center;
  margin: 0 auto 18px;
  width: fit-content;
}

.stamp-circle {
  border-radius: 50%;
  box-sizing: border-box;
}

.pass-field {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}

.pass-field-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 3px;
  opacity: 0.95;
}

.pass-field-value {
  font-size: 15px;
  font-weight: 500;
  word-break: break-word;
}

.pass-qr-wrap {
  display: flex;
  justify-content: center;
  margin-top: auto;
  padding-top: 14px;
}

.pass-qr-box {
  width: 130px;
  height: 130px;
  background: #ffffff;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.pass-qr-svg {
  width: 100%;
  height: 100%;
  display: block;
  shape-rendering: crispEdges;
}
</style>
